/* eslint-disable no-console */
import { calcularImporteNetoIntermediario } from "."; // ajustá la ruta si es necesario
import { AGENCIAS_DIRECTAS, SIN_COMISION_2025 } from "../constants/permisos";
import { getApxMonto } from "./apx";
import { calcularComisionEdificiosTotal, resolverFechaFactura } from "./comisionEdificiosUtil";
import { getImporteFacturable } from "./getImporteFacturable";
import { resolverPorcentajeAgencia } from "./resolverPorcentajeAgencia";
import { resolverPorcentajeBroker } from "./resolverPorcentajeBroker";

// Fecha de corte para ID 32
const EXECUTIVE_CUTOFF_DATE = new Date("2025-06-01");

// Usa resolverFechaFactura centralizada y retorna Date
const resolverFechaComision = (op) => new Date(resolverFechaFactura(op));

export const calculateComission = {
  /**
   * Comisión de agencia (intermediario).
   * Preferir pasar opportunity con importe ya en moneda local (oppEnLocal).
   * tipoCambio es legacy — solo lo usa VentasFacturasEjecutivo.
   */
  calculateAgencyCommission: (opportunity, clientes, intermediarios, tipoCambio, pctMap = null) => {
    let comisionIntermediarioPorcentaje = 0;
    // Anti-doble-descuento (7.22 Paso 2): con descuento de acuerdo, sin comisión de agencia
    // (el 25% ya salió como reducción de base vía getImporteFacturable).
    if ((parseFloat(opportunity.descuento_acuerdo_pct) || 0) > 0) return 0;
    if (!opportunity.isBTL) {
      // % de agencia desde la fuente única (Fase A0); con pctMap (A3) lee el % congelado.
      comisionIntermediarioPorcentaje = resolverPorcentajeAgencia(
        opportunity,
        clientes,
        intermediarios,
        opportunity.pais,
        pctMap
      );
    } else {
      comisionIntermediarioPorcentaje = opportunity.comision || 0;
      if (AGENCIAS_DIRECTAS.includes(opportunity.intermediarioId || opportunity.intermediario))
        comisionIntermediarioPorcentaje = 0;
    }
    const importeBruto = parseFloat(opportunity.importe) || 0;
    const comision = importeBruto * (comisionIntermediarioPorcentaje / 100);
    // Legacy: si el caller pasa tipoCambio y la opp está en USD, convierte el resultado
    if (tipoCambio && opportunity.moneda_dolar) return comision * tipoCambio;
    return comision;
  },

  /**
   * Comisión comercial total (para ER y cálculos).
   * Preferir pasar opportunity con importe ya en moneda local (oppEnLocal).
   * tipoCambio es legacy — solo lo usa VentasFacturasEjecutivo.
   */
  calculateSalesCommission: (
    opportunity,
    clientes,
    intermediarios,
    edificios,
    tipoCambio,
    snapMap = null,
    takeLocal = 0,
    pctMap = null
  ) => {
    const agencyCommission = calculateComission.calculateAgencyCommission(
      opportunity,
      clientes,
      intermediarios,
      tipoCambio,
      pctMap
    );
    const netoDespuesAgencias = calcularImporteNetoIntermediario(
      opportunity,
      Object.values(clientes),
      Object.values(intermediarios),
      opportunity.pais,
      pctMap
    );
    // FASE 6 (capa aislada, regla 10.48): el take programático baja la base de comisión de
    // edificios y la de comisión comercial. `takeLocal` viene del call site ya en moneda local
    // (getTakeLocal). Default 0 → idéntico al comportamiento actual.
    // APX (Publicis, etapa 2): capa paralela auto-resuelta desde opportunity.apx_id (a diferencia
    // del take, que viene de un map externo). `getApxMonto` devuelve 0 si apx_id es null → inerte.
    const apxLocal = getApxMonto(opportunity, intermediarios, pctMap);
    // Broker AdLab (2ª capa, ver 7.13): baja la base de la comisión comercial igual que agencia/APX/
    // edificios (regla: comercial sobre el neto que descuenta TODA la intermediación). El % (post-gate;
    // congelado si hay pctMap) sale de la fuente única; 0 si no aplica (sin broker_id, histórica, o
    // excluir_broker) → brokerLocal 0 → neto idéntico a hoy.
    const brokerPct = resolverPorcentajeBroker(
      opportunity,
      Object.values(clientes),
      Object.values(intermediarios),
      resolverFechaComision(opportunity),
      pctMap
    );
    const brokerLocal = (getImporteFacturable(opportunity) * brokerPct) / 100;
    const buildingCommission = calcularComisionEdificiosTotal(
      opportunity,
      edificios,
      netoDespuesAgencias,
      snapMap,
      takeLocal,
      apxLocal
    );
    // Base = importe FACTURABLE (7.22): con descuento de acuerdo baja a importe×(1−desc); sin él,
    // getImporteFacturable devuelve el importe → netoFinal idéntico a hoy para agencias reales.
    const importeBruto = getImporteFacturable(opportunity);
    const netoFinal = importeBruto - agencyCommission - buildingCommission - (takeLocal || 0) - apxLocal - brokerLocal;
    if (netoFinal <= 0) return 0;

    // Parseo de mes_cierre "MM-YYYY"
    let monthCierre = null;
    let yearCierre = null;
    if (opportunity.mes_cierre) {
      const [mesStr, anoStr] = opportunity.mes_cierre.split("-");
      monthCierre = parseInt(mesStr, 10) - 1; // 01 → 0
      yearCierre = parseInt(anoStr, 10);
    }

    const fechaFactura = resolverFechaComision(opportunity);
    const ejecutivoId = parseInt(opportunity.ejecutivo);

    // Exclusión ID 32 (aplica a ambos países)
    if (ejecutivoId === 32 && fechaFactura >= EXECUTIVE_CUTOFF_DATE) {
      return 0;
    }

    let totalComision = 0;

    if (opportunity.pais === "Perú") {
      // Priscilla (ID 7): siempre 1.25% universal
      totalComision += netoFinal * 0.0125;

      // ID 6: 3.75% si mes_cierre <= junio 2025 (dejó la empresa)
      if (yearCierre === 2025 && monthCierre <= 5) {
        totalComision += netoFinal * 0.0375;
      }

      // Karin (ID 306): 4% cuentas bajo su gestión, 1% resto — desde julio 2025. Corte cuentas cedidas
      // (7.13): si la venta paga broker (brokerPct > 0), la interna de Karin es 0 — mutuamente excluyente
      // con el 10% de AdLab (corta 4% y 1%; respeta excluir_broker/históricas por el mismo resolver).
      if (brokerPct <= 0 && ((yearCierre === 2025 && monthCierre >= 6) || yearCierre > 2025)) {
        const clienteAsociado = clientes[opportunity.client_id];
        const esGestionKarin = clienteAsociado && String(clienteAsociado.ejecutivo) === "306";
        const percentage306 = esGestionKarin ? 4 : 1;
        totalComision += netoFinal * (percentage306 / 100);
      }
    } else if (opportunity.pais === "Chile") {
      // Año de facturación: facturafecha real, o año planificado (ano) para pendientes
      const yearFactura = opportunity.facturafecha
        ? fechaFactura.getFullYear()
        : parseInt(opportunity.ano, 10) || fechaFactura.getFullYear();

      if (yearFactura <= 2025) {
        // Lógica 2025: escala descendente por trimestre
        if (!opportunity.ejecutivo) return 0;
        if (SIN_COMISION_2025.includes(ejecutivoId)) return 0;

        const monthFactura = fechaFactura.getMonth();
        let percentage = 0;
        if (yearFactura === 2025) {
          if (monthFactura <= 2) percentage = 8;
          else if (monthFactura <= 5) percentage = 7;
          else if (monthFactura <= 8) percentage = 6;
          else percentage = 5;
        }
        totalComision += netoFinal * (percentage / 100);
      } else {
        // 2026+: Nicolás (904) cobra solo en ventas CERRADAS (mes_cierre) en 2026+
        if (yearCierre && yearCierre >= 2026) {
          const clienteAsociado = clientes[opportunity.client_id];
          const esGestionNicolas = clienteAsociado && String(clienteAsociado.ejecutivo) === "904";
          const percentage = esGestionNicolas ? 4 : 1;
          totalComision += netoFinal * (percentage / 100);
        }
        // Ventas cerradas en 2025 facturadas en 2026+: $0 comisión comercial
      }
    }

    return totalComision;
  },

  /**
   * Desglose de comisión comercial para modal (array de objetos).
   * Preferir pasar opportunity con importe ya en moneda local (oppEnLocal).
   * tipoCambio es legacy — solo lo usa VentasFacturasEjecutivo.
   */
  getSalesCommissionBreakdown: (
    opportunity,
    clientes,
    intermediarios,
    edificios,
    tipoCambio,
    snapMap = null,
    takeLocal = 0,
    pctMap = null
  ) => {
    const agencyCommission = calculateComission.calculateAgencyCommission(
      opportunity,
      clientes,
      intermediarios,
      tipoCambio,
      pctMap
    );
    const netoDespuesAgencias = calcularImporteNetoIntermediario(
      opportunity,
      Object.values(clientes),
      Object.values(intermediarios),
      opportunity.pais,
      pctMap
    );
    // FASE 6 (capa aislada, regla 10.48): mismo descuento del take + APX que calculateSalesCommission.
    const apxLocal = getApxMonto(opportunity, intermediarios, pctMap);
    // Broker AdLab (2ª capa, ver 7.13): mismo descuento que calculateSalesCommission (baja la base de la
    // comisión comercial). 0 si no aplica → neto idéntico a hoy.
    const brokerPct = resolverPorcentajeBroker(
      opportunity,
      Object.values(clientes),
      Object.values(intermediarios),
      resolverFechaComision(opportunity),
      pctMap
    );
    const brokerLocal = (getImporteFacturable(opportunity) * brokerPct) / 100;
    const buildingCommission = calcularComisionEdificiosTotal(
      opportunity,
      edificios,
      netoDespuesAgencias,
      snapMap,
      takeLocal,
      apxLocal
    );
    // Base = importe FACTURABLE (7.22): mismo criterio que calculateSalesCommission.
    const importeBruto = getImporteFacturable(opportunity);
    const netoFinal = importeBruto - agencyCommission - buildingCommission - (takeLocal || 0) - apxLocal - brokerLocal;
    if (netoFinal <= 0) return [];

    let monthCierre = null;
    let yearCierre = null;
    if (opportunity.mes_cierre) {
      const [mesStr, anoStr] = opportunity.mes_cierre.split("-");
      monthCierre = parseInt(mesStr, 10) - 1;
      yearCierre = parseInt(anoStr, 10);
    }

    const fechaFactura = resolverFechaComision(opportunity);
    const ejecutivoId = parseInt(opportunity.ejecutivo);

    // Exclusión ID 32
    if (ejecutivoId === 32 && fechaFactura >= EXECUTIVE_CUTOFF_DATE) return [];

    const desglose = [];

    if (opportunity.pais === "Perú") {
      // Priscilla (ID 7) siempre
      desglose.push({ ejecutivo: 7, percentage: 1.25, comision: netoFinal * 0.0125 });

      // ID 6 si aplica
      if (yearCierre === 2025 && monthCierre <= 5) {
        desglose.push({ ejecutivo: 6, percentage: 3.75, comision: netoFinal * 0.0375 });
      }

      // Karin (ID 306): 4% gestión / 1% resto — desde julio 2025. Corte cuentas cedidas (7.13): si la
      // venta paga broker (brokerPct > 0), la interna de Karin es 0 — mutuamente excluyente con AdLab.
      if (brokerPct <= 0 && ((yearCierre === 2025 && monthCierre >= 6) || yearCierre > 2025)) {
        const clienteAsociado = clientes[opportunity.client_id];
        const esGestionKarin = clienteAsociado && String(clienteAsociado.ejecutivo) === "306";
        const percentage306 = esGestionKarin ? 4 : 1;
        desglose.push({ ejecutivo: 306, percentage: percentage306, comision: netoFinal * (percentage306 / 100) });
      }
    } else if (opportunity.pais === "Chile") {
      // Año de facturación: facturafecha real, o año planificado (ano) para pendientes
      const yearFactura = opportunity.facturafecha
        ? fechaFactura.getFullYear()
        : parseInt(opportunity.ano, 10) || fechaFactura.getFullYear();

      if (yearFactura <= 2025) {
        // Lógica 2025
        if (!opportunity.ejecutivo) return [];
        if (SIN_COMISION_2025.includes(ejecutivoId)) return [];

        const monthFactura = fechaFactura.getMonth();
        let percentage = 0;
        if (yearFactura === 2025) {
          if (monthFactura <= 2) percentage = 8;
          else if (monthFactura <= 5) percentage = 7;
          else if (monthFactura <= 8) percentage = 6;
          else percentage = 5;
        }
        if (percentage > 0) {
          desglose.push({ ejecutivo: ejecutivoId, percentage, comision: netoFinal * (percentage / 100) });
        }
      } else {
        // 2026+: Nicolás (904) cobra solo en ventas CERRADAS (mes_cierre) en 2026+
        if (yearCierre && yearCierre >= 2026) {
          const clienteAsociado = clientes[opportunity.client_id];
          const esGestionNicolas = clienteAsociado && String(clienteAsociado.ejecutivo) === "904";
          const percentage = esGestionNicolas ? 4 : 1;
          desglose.push({ ejecutivo: 904, percentage, comision: netoFinal * (percentage / 100) });
        }
        // Ventas cerradas en 2025 facturadas en 2026+: $0 comisión comercial
      }
    }

    // Si el ejecutivo de la oportunidad no está en el desglose, agregarlo con 0%
    // (ej: Sergio cerró la venta pero dejó la empresa → aparece con $0)
    if (ejecutivoId && !desglose.some((d) => d.ejecutivo === ejecutivoId)) {
      desglose.unshift({ ejecutivo: ejecutivoId, percentage: 0, comision: 0 });
    }

    return desglose;
  },
};
